import React, { useState, useMemo } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { RatioData } from './data/projectsData';
import {
  computeAllLotsStats,
  computeRiskScore,
  LotDistribution,
} from './data/ratioStats';
import Tooltip from './Ratio_Tooltip';

interface MaitriseBudgetProps {
  allProjects: RatioData[];
}

const fmt = (v: number) => v.toLocaleString('fr-FR');
const fmtK = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M€`;
  if (Math.abs(v) >= 1_000) return `${Math.round(v / 1_000).toLocaleString('fr-FR')} k€`;
  return `${Math.round(v).toLocaleString('fr-FR')} €`;
};

// ─── Mini barre inline ────────────────────────────────────────────────────────
const iqrBg  = (cv: number) => cv < 0.2 ? 'bg-green-200'   : cv < 0.4 ? 'bg-violet-200'   : 'bg-orange-200';
const medCol = (cv: number) => cv < 0.2 ? 'bg-green-600'   : cv < 0.4 ? 'bg-violet-600'   : 'bg-orange-500';
const medTxt = (cv: number) => cv < 0.2 ? 'text-green-700' : cv < 0.4 ? 'text-violet-700' : 'text-orange-600';

const MiniBar: React.FC<{ dist: LotDistribution }> = ({ dist }) => {
  const span = dist.max - dist.min;
  const toP = (v: number) => span > 0 ? Math.min(100, Math.max(0, (v - dist.min) / span * 100)) : 50;
  return (
    <div className="relative h-1.5 w-full bg-gray-200 rounded-full">
      <div
        className={`absolute top-0 h-1.5 ${iqrBg(dist.cv)} rounded-full`}
        style={{ left: `${toP(dist.p25)}%`, width: `${Math.max(toP(dist.p75) - toP(dist.p25), 3)}%` }}
      />
      <div
        className={`absolute w-0.5 h-3 -top-[3px] ${medCol(dist.cv)} rounded-full`}
        style={{ left: `${toP(dist.median)}%`, transform: 'translateX(-50%)' }}
      />
    </div>
  );
};

// ─── Types internes ───────────────────────────────────────────────────────────

interface EntrepriseStats {
  nom: string;
  nbLots: number;
  positionMoyenne: number;
  pctAbove15: number;
  pctCompetitif: number;
  indiceAgressivite: number;
  lotDetails: Array<{
    lotName: string;
    ratioM2: number;
    median: number;
    ecartPct: number;
  }>;
}

// ─── Composant principal ─────────────────────────────────────────────────────

const MaitriseBudget: React.FC<MaitriseBudgetProps> = ({ allProjects }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['choc', 'heatmap', 'entreprises'])
  );

  const avgSurface = useMemo(() => {
    if (allProjects.length === 0) return 1000;
    return Math.round(allProjects.reduce((s, p) => s + p.surface, 0) / allProjects.length);
  }, [allProjects]);

  const lotsStats = useMemo(() => computeAllLotsStats(allProjects), [allProjects]);
  const lotsArray = useMemo(() => Array.from(lotsStats.values()), [lotsStats]);

  const lotsWithRisk = useMemo(() =>
    lotsArray
      .map(dist => ({ dist, riskScore: computeRiskScore(dist, lotsArray) }))
      .sort((a, b) => b.riskScore - a.riskScore),
    [lotsArray]
  );

  // ── Simulations de gain ──
  const simulations = useMemo(() => {
    const gainMedian = lotsArray.reduce((s, d) =>
      s + Math.max(0, d.p75 - d.median) * avgSurface, 0);
    const gainBestPrice = lotsArray.reduce((s, d) =>
      s + Math.max(0, d.median - d.min) * avgSurface, 0);
    const top10 = [...lotsArray]
      .map(d => ({ dist: d, enjeu: (d.p75 - d.p25) * d.weightPct }))
      .sort((a, b) => b.enjeu - a.enjeu)
      .slice(0, 10);
    const gainTop10 = top10.reduce((s, { dist: d }) =>
      s + Math.max(0, d.median - d.p25) * avgSurface, 0);
    return { gainMedian, gainBestPrice, gainTop10 };
  }, [lotsArray, avgSurface]);

  // ── Concentration du risque ──
  const riskConcentration = useMemo(() => {
    const totalEnjeu = lotsWithRisk.reduce((s, l) =>
      s + (l.dist.p75 - l.dist.p25) * avgSurface, 0);
    let cumul = 0;
    let topN = 0;
    for (const l of lotsWithRisk) {
      cumul += (l.dist.p75 - l.dist.p25) * avgSurface;
      topN++;
      if (totalEnjeu > 0 && cumul / totalEnjeu >= 0.62) break;
    }
    return { topN, pct: totalEnjeu > 0 ? Math.round(cumul / totalEnjeu * 100) : 0 };
  }, [lotsWithRisk, avgSurface]);

  // ── Classement entreprises ──
  const entreprisesStats = useMemo(() => {
    const map = new Map<string, Array<{ lotName: string; ratioM2: number; projetId: string }>>();
    allProjects.forEach(p => {
      p.lots.forEach(l => {
        if (!l.entreprise) return;
        if (!map.has(l.entreprise)) map.set(l.entreprise, []);
        map.get(l.entreprise)!.push({ lotName: l.nom, ratioM2: l.ratioM2, projetId: p.id });
      });
    });

    const result: EntrepriseStats[] = [];
    map.forEach((entries, nom) => {
      const lotDetails: EntrepriseStats['lotDetails'] = [];
      let totalRank = 0;
      let rankCount = 0;
      let nbAbove15 = 0;
      let nbCompetitif = 0;

      entries.forEach(entry => {
        const stats = lotsStats.get(entry.lotName);
        if (!stats) return;
        const ecartPct = stats.median > 0
          ? Math.round((entry.ratioM2 - stats.median) / stats.median * 100) : 0;
        lotDetails.push({ lotName: entry.lotName, ratioM2: entry.ratioM2, median: stats.median, ecartPct });
        if (ecartPct > 15) nbAbove15++;
        if (entry.ratioM2 < stats.p25) nbCompetitif++;
        const allRatios = allProjects
          .flatMap(p => p.lots.filter(l => l.nom === entry.lotName).map(l => l.ratioM2))
          .sort((a, b) => a - b);
        const rank = allRatios.indexOf(entry.ratioM2) + 1;
        totalRank += rank / allRatios.length;
        rankCount++;
      });

      const nbLots = lotDetails.length;
      if (nbLots === 0) return;
      const positionMoyenne = rankCount > 0 ? totalRank / rankCount : 0.5;
      const pctAbove15 = Math.round(nbAbove15 / nbLots * 100);
      const pctCompetitif = Math.round(nbCompetitif / nbLots * 100);
      const indiceAgressivite = Math.round(
        (pctCompetitif / 100 * 0.7 + (1 - positionMoyenne) * 0.3) * 100
      );
      result.push({ nom, nbLots, positionMoyenne, pctAbove15, pctCompetitif, indiceAgressivite, lotDetails });
    });
    return result.sort((a, b) => b.indiceAgressivite - a.indiceAgressivite);
  }, [allProjects, lotsStats]);

  // ── Zones d'incohérence ──
  const incoherences = useMemo(() => {
    return entreprisesStats
      .map(e => {
        const ecarts = e.lotDetails.map(l => l.ecartPct);
        const meanEcart = ecarts.reduce((s, v) => s + v, 0) / ecarts.length;
        const variance = ecarts.reduce((s, v) => s + (v - meanEcart) ** 2, 0) / ecarts.length;
        const volatilite = Math.round(Math.sqrt(variance));

        const lotsSous = e.lotDetails.filter(l => l.ecartPct < 0);
        const pctSousMediane = Math.round(lotsSous.length / e.lotDetails.length * 100);
        const anomalies = e.lotDetails.filter(l => l.ecartPct > 20).sort((a, b) => b.ecartPct - a.ecartPct);

        // Surcoût estimé = Σ (ratioM2 - médiane) × surface pour les lots au-dessus
        const surcoutEstime = Math.round(
          anomalies.reduce((s, a) => s + Math.max(0, a.ratioM2 - a.median) * avgSurface, 0)
        );

        // Économie si corrigé = ramener les lots anormaux à la médiane
        const economieSiCorrige = surcoutEstime;

        // Score d'incohérence 0-100
        // = volatilité normalisée (40%) + magnitude max anomalie (30%) + ratio lots touchés (30%)
        const maxAnomalie = anomalies.length > 0 ? anomalies[0].ecartPct : 0;
        const ratioTouches = anomalies.length / e.lotDetails.length;
        const scoreIncoherence = Math.min(100, Math.round(
          Math.min(volatilite / 30, 1) * 40 +
          Math.min(maxAnomalie / 60, 1) * 30 +
          ratioTouches * 30
        ));

        return {
          ...e,
          pctSousMediane,
          anomalies,
          volatilite,
          surcoutEstime,
          economieSiCorrige,
          scoreIncoherence,
        };
      })
      // Détecter : (1) compétitif globalement mais anomalies, OU (2) forte volatilité interne
      .filter(e => (e.pctSousMediane > 40 && e.anomalies.length > 0) || e.volatilite > 20)
      .sort((a, b) => b.scoreIncoherence - a.scoreIncoherence);
  }, [entreprisesStats, avgSurface]);

  // ── Top 10 anomalies ──
  const topAnomalies = useMemo(() => {
    const items = [...lotsArray]
      .map(d => ({
        lotName: d.lotName,
        ecartPct: d.median > 0 ? Math.round((d.p75 - d.p25) / d.median * 100) : 0,
        enjeu: Math.round((d.p75 - d.p25) * avgSurface),
        nbProjets: d.nbProjets,
      }))
      .sort((a, b) => b.enjeu - a.enjeu)
      .slice(0, 10);
    const maxEnjeu = Math.max(...items.map(a => a.enjeu), 1);
    return items.map(a => ({ ...a, barPct: Math.round(a.enjeu / maxEnjeu * 100) }));
  }, [lotsArray, avgSurface]);

  const simMax = Math.max(simulations.gainMedian, simulations.gainBestPrice, simulations.gainTop10, 1);

  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });
  };

  return (
    <div className="space-y-5">

      {/* ── KPI strip ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiPill label="Projets analysés" value={`${allProjects.length}`} />
        <KpiPill label="Surface moyenne" value={`${fmt(avgSurface)} m²`} />
        <KpiPill label="Lots identifiés" value={`${lotsArray.length}`} />
        <KpiPill
          label="Lots à risque"
          value={`${riskConcentration.topN}`}
          accent="red"
          sub={`${riskConcentration.pct}% du risque`}
        />
      </div>

      {/* ── Section 1 — Simulation de gain ────────────────────────────── */}
      {lotsArray.length > 0 && (
        <Section
          id="choc"
          title="Simulation de gain potentiel"
          expanded={expandedSections.has('choc')}
          onToggle={toggleSection}
          accent="violet"
          sub={`Base ${fmt(avgSurface)} m² (surface moyenne)`}
        >
          <div className="p-5 space-y-4">
            {/* Barres horizontales comparatives */}
            <div className="space-y-3">
              <SimBar
                label="Tout ramené au médian"
                value={simulations.gainMedian}
                pct={Math.round(simulations.gainMedian / simMax * 100)}
                color="bg-violet-500"
                tooltip={`Si chaque lot au-dessus de la médiane (P75 → médiane) est corrigé.\nΣ max(0, P75 − médiane) × ${fmt(avgSurface)} m²`}
              />
              <SimBar
                label="Meilleur prix par lot"
                value={simulations.gainBestPrice}
                pct={Math.round(simulations.gainBestPrice / simMax * 100)}
                color="bg-green-500"
                tooltip={`Théorique : chaque lot au prix minimum observé.\nΣ (médiane − min) × ${fmt(avgSurface)} m²\n\nAucun projet réel n'atteint le minimum partout.`}
              />
              <SimBar
                label="Top 10 lots corrigés"
                value={simulations.gainTop10}
                pct={Math.round(simulations.gainTop10 / simMax * 100)}
                color="bg-blue-500"
                tooltip={`Focus : 10 lots avec le plus d'enjeu (dispersion × poids), ramenés au P25.\nApproche pragmatique 80/20.`}
              />
            </div>
          </div>
        </Section>
      )}

      {/* ── Section 2 — Carte de risque ───────────────────────────────── */}
      {lotsWithRisk.length > 0 && (
        <Section
          id="heatmap"
          title="Carte de risque par lot"
          expanded={expandedSections.has('heatmap')}
          onToggle={toggleSection}
          accent="red"
          badge={`${riskConcentration.topN} lots = ${riskConcentration.pct}% du risque`}
        >
          <div className="px-5 pt-3 pb-1">
            <p className="text-xs text-gray-500">
              Les <span className="font-semibold text-red-600">{riskConcentration.topN} premiers lots</span> concentrent{' '}
              <span className="font-semibold text-red-600">{riskConcentration.pct}%</span> du risque financier.
              Score = dispersion × poids budgétaire.
            </p>
          </div>

          {/* En-tête fixe */}
          <div className="flex items-center gap-3 px-5 py-2 text-xs text-gray-400 font-medium border-b border-gray-100">
            <div className="w-4 shrink-0" />
            <div className="flex-1">Lot</div>
            <div className="hidden sm:block w-36 text-right shrink-0">P25 · Méd. · P75</div>
            <div className="w-20 shrink-0 text-center">Dispersion</div>
            <div className="w-16 text-right shrink-0">Marge</div>
            <div className="w-20 text-right shrink-0">Enjeu</div>
            <div className="w-10 text-right shrink-0">Score</div>
          </div>

          {/* Liste scrollable */}
          <div className="max-h-[340px] overflow-y-auto divide-y divide-gray-50">
            {lotsWithRisk.map(({ dist, riskScore }, i) => {
              const negoPct = dist.median > 0
                ? Math.round((dist.median - dist.p25) / dist.median * 100) : 0;
              const enjeu = Math.round((dist.p75 - dist.p25) * avgSurface);
              const isTop = i < riskConcentration.topN;

              return (
                <div
                  key={dist.lotName}
                  className={`flex items-center gap-3 px-5 py-2.5 transition-colors ${
                    isTop ? 'bg-red-50/30 hover:bg-red-50/60' : 'hover:bg-gray-50/50'
                  }`}
                >
                  <div className="w-4 shrink-0 text-center">
                    {isTop && <span className="text-xs font-bold text-red-400">{i + 1}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-800">{dist.lotName}</div>
                    <div className="w-24 mt-1"><MiniBar dist={dist} /></div>
                  </div>
                  <div className="hidden sm:flex items-center gap-0.5 text-xs tabular-nums w-36 justify-end shrink-0">
                    <span className="text-gray-400">{fmt(dist.p25)}</span>
                    <span className="text-gray-200 mx-0.5">·</span>
                    <span className={`font-semibold ${medTxt(dist.cv)}`}>{fmt(dist.median)}</span>
                    <span className="text-gray-200 mx-0.5">·</span>
                    <span className="text-gray-400">{fmt(dist.p75)}</span>
                  </div>
                  <div className="w-20 text-center shrink-0">
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                      dist.cv > 0.3 ? 'bg-red-50 text-red-600'
                        : dist.cv > 0.2 ? 'bg-orange-50 text-orange-600'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      ±{dist.median > 0 ? Math.round((dist.p75 - dist.p25) / dist.median * 100) : 0}%
                    </span>
                  </div>
                  <div className="w-16 text-right shrink-0">
                    {negoPct >= 3
                      ? <span className="text-xs font-bold text-green-600">↓{negoPct}%</span>
                      : <span className="text-xs text-gray-200">—</span>
                    }
                  </div>
                  <div className="w-20 text-right shrink-0">
                    <span className={`text-xs font-semibold ${enjeu > 50000 ? 'text-red-600' : 'text-gray-500'}`}>
                      {fmtK(enjeu)}
                    </span>
                  </div>
                  <div className="w-10 text-right shrink-0">
                    <span className={`text-xs font-bold ${
                      riskScore >= 65 ? 'text-red-600' : riskScore >= 35 ? 'text-orange-500' : 'text-green-600'
                    }`}>{riskScore}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {lotsWithRisk.length > 8 && (
            <div className="px-5 py-2 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
              <p className="text-xs text-gray-400 text-center">{lotsWithRisk.length} lots au total — scrollez pour voir tous</p>
            </div>
          )}
        </Section>
      )}

      {/* ── Section 3 — Classement entreprises ────────────────────────── */}
      {entreprisesStats.length > 0 && (
        <Section
          id="entreprises"
          title="Classement des entreprises"
          expanded={expandedSections.has('entreprises')}
          onToggle={toggleSection}
          accent="blue"
          badge={`${entreprisesStats.length} entreprises`}
        >
          <div>
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-gray-500">
                  <th className="text-left px-5 py-2.5 font-medium">Entreprise</th>
                  <th className="text-right px-3 py-2.5 font-medium">Lots</th>
                  <th className="text-right px-3 py-2.5 font-medium">
                    <Tooltip text="% des lots où l'entreprise est en dessous du 1er quartile (P25) — très compétitif" position="bottom" width="w-56">
                      <span className="cursor-help underline underline-offset-2 decoration-dotted">% compétitif</span>
                    </Tooltip>
                  </th>
                  <th className="text-right px-3 py-2.5 font-medium">
                    <Tooltip text="% des lots où l'entreprise est > 15% au-dessus de la médiane — points de vigilance" position="bottom" width="w-56">
                      <span className="cursor-help underline underline-offset-2 decoration-dotted">{'>'}15% méd.</span>
                    </Tooltip>
                  </th>
                  <th className="text-right px-5 py-2.5 font-medium">
                    <Tooltip text="Indice 0-100 : 70% basé sur le % de lots compétitifs + 30% sur la position moyenne" position="bottom" width="w-64">
                      <span className="cursor-help underline underline-offset-2 decoration-dotted">Agressivité</span>
                    </Tooltip>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {entreprisesStats.map((e, i) => (
                  <tr key={e.nom} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50/40 transition-colors`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                          e.indiceAgressivite >= 60 ? 'bg-green-400'
                            : e.indiceAgressivite >= 40 ? 'bg-blue-400'
                            : 'bg-gray-300'
                        }`} />
                        <span className="font-medium text-gray-800">{e.nom}</span>
                      </div>
                    </td>
                    <td className="text-right px-3 py-3 text-gray-500 tabular-nums">{e.nbLots}</td>
                    <td className="text-right px-3 py-3">
                      <span className={`font-semibold tabular-nums ${
                        e.pctCompetitif >= 40 ? 'text-green-600'
                          : e.pctCompetitif >= 20 ? 'text-blue-600' : 'text-gray-400'
                      }`}>{e.pctCompetitif}%</span>
                    </td>
                    <td className="text-right px-3 py-3">
                      <span className={`font-semibold tabular-nums ${
                        e.pctAbove15 >= 30 ? 'text-red-600'
                          : e.pctAbove15 >= 15 ? 'text-orange-500' : 'text-gray-400'
                      }`}>{e.pctAbove15}%</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              e.indiceAgressivite >= 60 ? 'bg-green-500'
                                : e.indiceAgressivite >= 40 ? 'bg-blue-500' : 'bg-gray-300'
                            }`}
                            style={{ width: `${e.indiceAgressivite}%` }}
                          />
                        </div>
                        <span className={`font-bold tabular-nums w-6 text-right ${
                          e.indiceAgressivite >= 60 ? 'text-green-600'
                            : e.indiceAgressivite >= 40 ? 'text-blue-600' : 'text-gray-500'
                        }`}>{e.indiceAgressivite}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* ── Section 4 — Zones d'incohérence ──────────────────────────── */}
      {incoherences.length > 0 && (
        <Section
          id="incoherences"
          title="Zones d'incohérence"
          expanded={expandedSections.has('incoherences')}
          onToggle={toggleSection}
          accent="orange"
          badge={`${incoherences.length} alerte${incoherences.length > 1 ? 's' : ''}`}
        >
          <div className="px-5 pt-3 pb-1">
            <p className="text-xs text-gray-500">
              Entreprises avec un comportement tarifaire incohérent — compétitives globalement mais avec des postes
              anormalement chers. Signal de compensation tarifaire ou opportunité de négociation ciblée.
            </p>
          </div>

          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="text-left px-5 py-2.5 font-medium">Entreprise</th>
                <th className="text-right px-3 py-2.5 font-medium">
                  <Tooltip text="Écart-type des écarts à la médiane par lot. Plus c'est élevé, plus le comportement tarifaire est erratique." position="bottom" width="w-56">
                    <span className="cursor-help underline underline-offset-2 decoration-dotted">Volatilité</span>
                  </Tooltip>
                </th>
                <th className="text-right px-3 py-2.5 font-medium">
                  <Tooltip text="Montant estimé payé en trop sur les lots anormaux (> +20% médiane) × surface moyenne" position="bottom" width="w-56">
                    <span className="cursor-help underline underline-offset-2 decoration-dotted">Surcoût</span>
                  </Tooltip>
                </th>
                <th className="text-center px-3 py-2.5 font-medium">
                  <Tooltip text="% de lots sous la médiane — indique la compétitivité globale" position="bottom" width="w-48">
                    <span className="cursor-help underline underline-offset-2 decoration-dotted">Sous méd.</span>
                  </Tooltip>
                </th>
                <th className="text-right px-5 py-2.5 font-medium">
                  <Tooltip text="Score 0-100 : volatilité (40%) + magnitude max anomalie (30%) + ratio lots touchés (30%)" position="bottom" width="w-60">
                    <span className="cursor-help underline underline-offset-2 decoration-dotted">Score</span>
                  </Tooltip>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {incoherences.map((e, i) => (
                <React.Fragment key={e.nom}>
                  <tr className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-orange-50/40 transition-colors`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <ExclamationTriangleIcon className={`w-3.5 h-3.5 shrink-0 ${
                          e.scoreIncoherence >= 60 ? 'text-red-500' : 'text-orange-400'
                        }`} />
                        <div className="min-w-0">
                          <span className="font-medium text-gray-800">{e.nom}</span>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {e.anomalies.slice(0, 4).map(a => (
                              <span key={a.lotName} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                                a.ecartPct > 40 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                              }`}>
                                +{a.ecartPct}% {a.lotName}
                              </span>
                            ))}
                            {e.anomalies.length > 4 && (
                              <span className="text-[10px] text-gray-400 px-1 py-0.5">+{e.anomalies.length - 4} autres</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-right px-3 py-3 tabular-nums">
                      <span className={`font-semibold ${
                        e.volatilite >= 30 ? 'text-red-600' : e.volatilite >= 15 ? 'text-orange-600' : 'text-gray-500'
                      }`}>{e.volatilite}%</span>
                    </td>
                    <td className="text-right px-3 py-3 tabular-nums">
                      {e.surcoutEstime > 0
                        ? <span className="font-semibold text-red-600">{fmtK(e.surcoutEstime)}</span>
                        : <span className="text-gray-300">—</span>
                      }
                    </td>
                    <td className="text-center px-3 py-3 tabular-nums">
                      <span className={`font-semibold ${
                        e.pctSousMediane >= 60 ? 'text-green-600' : e.pctSousMediane >= 40 ? 'text-blue-600' : 'text-gray-400'
                      }`}>{e.pctSousMediane}%</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              e.scoreIncoherence >= 60 ? 'bg-red-500'
                                : e.scoreIncoherence >= 35 ? 'bg-orange-400' : 'bg-yellow-400'
                            }`}
                            style={{ width: `${e.scoreIncoherence}%` }}
                          />
                        </div>
                        <span className={`font-bold tabular-nums w-6 text-right ${
                          e.scoreIncoherence >= 60 ? 'text-red-600'
                            : e.scoreIncoherence >= 35 ? 'text-orange-600' : 'text-yellow-600'
                        }`}>{e.scoreIncoherence}</span>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {incoherences.length > 0 && (
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  Surcoût total estimé :{' '}
                  <span className="font-bold text-red-600">
                    {fmtK(incoherences.reduce((s, e) => s + e.surcoutEstime, 0))}
                  </span>
                </span>
                <span className="text-gray-400">
                  {incoherences.reduce((s, e) => s + e.anomalies.length, 0)} lots anormaux détectés
                </span>
              </div>
            </div>
          )}
        </Section>
      )}

      {/* ── Section 5 — Top 10 anomalies cross-lots ──────────────────── */}
      {topAnomalies.length > 0 && (
        <Section
          id="anomalies"
          title="Top 10 des anomalies"
          expanded={expandedSections.has('anomalies')}
          onToggle={toggleSection}
          accent="red"
          sub={`Enjeu total : ${fmtK(topAnomalies.reduce((s, a) => s + a.enjeu, 0))}`}
        >
          <div className="px-5 pt-3 pb-1">
            <p className="text-xs text-gray-500">
              Lots avec le plus d'enjeu financier (P25–P75 × {fmt(avgSurface)} m²). Chaque barre = une opportunité concrète.
            </p>
          </div>
          <div className="px-5 py-3 space-y-2">
            {topAnomalies.map((a, i) => (
              <div key={a.lotName} className="flex items-center gap-3">
                <span className={`w-5 text-right text-xs font-bold shrink-0 ${
                  i < 3 ? 'text-red-500' : 'text-gray-400'
                }`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-800 truncate">{a.lotName}</span>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                        a.ecartPct > 30 ? 'bg-red-50 text-red-600'
                          : a.ecartPct > 20 ? 'bg-orange-50 text-orange-600'
                          : 'bg-gray-100 text-gray-500'
                      }`}>±{a.ecartPct}%</span>
                      <span className="text-xs font-bold text-red-600 tabular-nums w-16 text-right">{fmtK(a.enjeu)}</span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        i === 0 ? 'bg-red-500' : i < 3 ? 'bg-orange-400' : 'bg-gray-300'
                      }`}
                      style={{ width: `${a.barPct}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {lotsArray.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-500">Aucun lot trouvé pour ces critères. Élargissez les filtres.</p>
        </div>
      )}
    </div>
  );
};

// ─── KPI pill (header) ──────────────────────────────────────────────────────

const KpiPill: React.FC<{ label: string; value: string; accent?: 'red'; sub?: string }> = ({ label, value, accent, sub }) => (
  <div className={`rounded-xl border px-4 py-3 ${
    accent === 'red' ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'
  }`}>
    <div className={`text-lg font-bold tabular-nums ${accent === 'red' ? 'text-red-600' : 'text-gray-800'}`}>{value}</div>
    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    {sub && <div className="text-xs text-red-500 font-medium mt-0.5">{sub}</div>}
  </div>
);

// ─── Section wrapper ────────────────────────────────────────────────────────

interface SectionProps {
  id: string;
  title: string;
  expanded: boolean;
  onToggle: (id: string) => void;
  accent: 'violet' | 'red' | 'blue' | 'orange' | 'green';
  badge?: string;
  sub?: string;
  children: React.ReactNode;
}

const ACCENT_DOT: Record<string, string> = {
  violet: 'bg-violet-400', red: 'bg-red-400', blue: 'bg-blue-400',
  orange: 'bg-orange-400', green: 'bg-green-400',
};

const Section: React.FC<SectionProps> = ({ id, title, expanded, onToggle, accent, badge, sub, children }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
    <button
      onClick={() => onToggle(id)}
      className={`w-full flex items-center gap-2 px-5 py-4 text-left hover:bg-gray-50 transition-colors ${!expanded ? 'rounded-b-2xl' : ''}`}
    >
      <span className={`w-2 h-2 rounded-full shrink-0 ${ACCENT_DOT[accent]}`} />
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
      {badge && (
        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full shrink-0">
          {badge}
        </span>
      )}
      {expanded
        ? <ChevronUpIcon className="w-4 h-4 text-gray-400 shrink-0" />
        : <ChevronDownIcon className="w-4 h-4 text-gray-400 shrink-0" />
      }
    </button>
    {expanded && <div className="border-t border-gray-100">{children}</div>}
  </div>
);

// ─── Barre de simulation ────────────────────────────────────────────────────

interface SimBarProps {
  label: string;
  value: number;
  pct: number;
  color: string;
  tooltip: string;
}

const SimBar: React.FC<SimBarProps> = ({ label, value, pct, color, tooltip }) => (
  <Tooltip text={tooltip} position="bottom" width="w-72" as="div">
    <div className="cursor-help group/sim">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <span className="text-sm font-bold text-gray-800">{fmtK(value)}</span>
      </div>
      <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-all group-hover/sim:brightness-110`}
          style={{ width: `${Math.max(pct, 3)}%` }}
        />
      </div>
    </div>
  </Tooltip>
);

export default MaitriseBudget;
